import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { doc, setDoc, increment, onSnapshot } from 'firebase/firestore';
import { CheckCircle, Lock } from 'lucide-react';

// Default candidates (fallback if no data in Firestore)
const defaultCandidates = [
  { id: 1, docId: "candidate_1", name: "Akram Fatahillah", school: "SMAIT ABU BAKAR" },
  { id: 2, docId: "candidate_2", name: "Ganik Gutami Romadhoni", school: "SMKN 1 PANJATAN" },
  { id: 3, docId: "candidate_3", name: "Raihan Akmal", school: "SMAN 1 WATES" }
];

function Vote() {
  const [hasVoted, setHasVoted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [isVotingOpen, setIsVotingOpen] = useState(true);
  const [candidates, setCandidates] = useState(defaultCandidates);

  useEffect(() => {
    // Listen to voting status
    const unsubscribeStatus = onSnapshot(doc(db, "config", "votingStatus"), (docSnap) => {
      if (docSnap.exists()) {
        setIsVotingOpen(docSnap.data().isOpen);
      } else {
        setIsVotingOpen(true);
      }
    });

    // Listen to candidates data from Firestore
    const unsubscribeCandidates = onSnapshot(doc(db, "votes", "candidates_data"), (docSnap) => {
      if (docSnap.exists() && docSnap.data().candidates) {
        const firestoreCandidates = docSnap.data().candidates.map((c, index) => ({
          ...c,
          id: c.id || index + 1,
          docId: c.docId || `candidate_${index + 1}`
        }));
        setCandidates(firestoreCandidates);
      } else {
        setCandidates(defaultCandidates);
      }
    });

    return () => {
      unsubscribeStatus();
      unsubscribeCandidates();
    };
  }, []);

  const handleVote = async (candidate) => {
    if (!isVotingOpen) {
      alert("Mohon maaf, sesi pemilihan sedang ditutup oleh panitia.");
      return;
    }
    if (hasVoted) return;

    if (!window.confirm(`Apakah Anda yakin ingin memilih ${candidate.name}?`)) {
      return;
    }

    setLoading(true);
    try {
      const candidateRef = doc(db, "votes", candidate.docId);

      await setDoc(candidateRef, {
        name: candidate.name,
        school: candidate.school,
        voteCount: increment(1)
      }, { merge: true });

      setHasVoted(true);
      setMessage(`Terima kasih! Anda telah memilih ${candidate.name}.`);

      setTimeout(() => {
        setHasVoted(false);
        setMessage("");
      }, 3000);

    } catch (error) {
      console.error("Error voting:", error);
      alert("Terjadi kesalahan saat menyimpan suara. Silakan coba lagi.");
    } finally {
      setLoading(false);
    }
  };

  if (!isVotingOpen && !hasVoted) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-6 text-center">
        <div className="bg-slate-900 border border-slate-800 p-8 rounded-2xl max-w-lg w-full shadow-2xl">
          <div className="mx-auto w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center mb-6">
            <Lock size={40} className="text-red-500" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4">Pemilihan Ditutup</h1>
          <p className="text-slate-400">
            Sesi pemilihan ketua saat ini sedang ditutup atau belum dimulai.
            Silakan tunggu instruksi selanjutnya dari panitia.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 pt-28 pb-16 px-4 sm:px-6 relative overflow-hidden">
      {/* Background Ambience */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 bg-indigo-600/15 rounded-full blur-[140px] pointer-events-none" />

      <div className="container mx-auto max-w-4xl relative z-10">
        <div className="text-center mb-12 space-y-3">
          <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full border border-indigo-500/30 bg-indigo-500/10 text-indigo-300 text-xs font-bold uppercase tracking-wider">
            Sistem E-Voting Suara Pelajar
          </div>
          <h1 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight">
            Pemilihan Ketua <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">FKPO</span>
          </h1>
          <p className="text-sm sm:text-base text-slate-400 max-w-xl mx-auto">
            Gunakan hak suaramu secara demokratis dan objektif demi kemajuan kepemimpinan pelajar Kulon Progo masa depan.
          </p>
        </div>

        {hasVoted ? (
          <div className="bg-slate-900/80 border border-emerald-500/40 rounded-3xl p-10 text-center backdrop-blur-xl shadow-2xl max-w-lg mx-auto animate-in fade-in zoom-in-95 duration-300">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-500/10 text-emerald-400 mb-6 border border-emerald-500/30 shadow-lg shadow-emerald-500/20">
              <CheckCircle size={44} />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Suara Berhasil Dicatat!</h2>
            <p className="text-slate-300 text-sm leading-relaxed">{message}</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
            {candidates.map((candidate) => (
              <div
                key={candidate.id}
                onClick={() => !loading && handleVote(candidate)}
                className={`
                  group relative bg-slate-900/60 hover:bg-slate-900/90 border border-slate-800/80 hover:border-indigo-500/50 rounded-2xl p-6 sm:p-7 cursor-pointer
                  transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:shadow-indigo-500/15
                  flex flex-col items-center text-center h-full backdrop-blur-md
                  ${loading ? 'opacity-50 pointer-events-none' : ''}
                `}
              >
                {/* Number Badge */}
                <div className="absolute top-4 right-4 w-9 h-9 rounded-xl bg-slate-800/80 border border-slate-700/80 text-slate-300 flex items-center justify-center font-extrabold text-sm group-hover:bg-indigo-600 group-hover:text-white group-hover:border-indigo-500 transition-all shadow-md">
                  {candidate.id}
                </div>

                {/* Avatar Placeholder */}
                <div className="w-24 h-24 rounded-2xl bg-gradient-to-tr from-indigo-950 via-slate-800 to-slate-800 border border-indigo-500/20 mb-5 flex items-center justify-center text-indigo-300 group-hover:border-indigo-400 group-hover:scale-105 transition-all shadow-xl">
                  <span className="text-3xl font-black">{candidate.name?.charAt(0) || '?'}</span>
                </div>

                <h3 className="text-lg font-bold text-white mb-1.5 group-hover:text-indigo-300 transition-colors">
                  {candidate.name}
                </h3>

                <p className="text-slate-400 text-xs sm:text-sm mb-6 flex-grow">
                  {candidate.school}
                </p>

                <button
                  className="w-full py-3 bg-slate-800/90 hover:bg-gradient-to-r hover:from-indigo-600 hover:to-purple-600 text-white rounded-xl font-semibold text-xs sm:text-sm transition-all border border-slate-700 group-hover:border-transparent group-hover:shadow-lg group-hover:shadow-indigo-600/30"
                >
                  Pilih Kandidat Ini
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Vote;

